import React, { useState } from "react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar, Users, Mail, Check, Send, Lock, Shield, Bell, UserPlus, ClipboardList, Home, BarChart3, Upload } from "lucide-react";
import { AttendanceRequestsTab } from "@/components/admin/tabs/AttendanceRequestsTab";
import { ParticipantsTab } from "@/components/admin/tabs/ParticipantsTab";
import { AdministratorsTab } from "@/components/admin/tabs/AdministratorsTab";
import { EventsTab } from "@/components/admin/tabs/EventsTab";
import { AttendeesTab } from "@/components/admin/tabs/AttendeesTab";
import { CalendarImportsTab } from "@/components/admin/tabs/CalendarImportsTab";
import { EmailTab } from "@/components/admin/tabs/EmailTab";
import { AlertsTab } from "@/components/admin/tabs/AlertsTab";
import { ApplicantsTab } from "@/components/admin/tabs/ApplicantsTab";
import { SignupsTab } from "@/components/admin/tabs/SignupsTab";
import { ReportsTab } from "@/components/admin/tabs/ReportsTab";
import { CSVImportDialog } from "@/components/admin/CSVImportDialog";
import { useAdminDashboard } from "@/components/admin/useAdminDashboard";

const AdminDashboard = () => {
  const {
    events,
    participants,
    administrators,
    mondaySyncStatus,
    scrapedEvents,
    attendanceRequests,
    loading,
    scanning,
    dialogOpen,
    setDialogOpen,
    sortField,
    sortDir,
    expandedRows,
    tableRef,
    attendeeSearch,
    setAttendeeSearch,
    attendeeSortField,
    setAttendeeSortField,
    attendeeSortDir,
    setAttendeeSortDir,
    uploading,
    syncingMonday,
    fileInputRef,
    editingEventId,
    editEventData,
    setEditEventData,
    withdrawDialogOpen,
    setWithdrawDialogOpen,
    withdrawReason,
    setWithdrawReason,
    withdrawingAttendance,
    currentAdminEmail,
    revokeApprovalDialogOpen,
    setRevokeApprovalDialogOpen,
    revokeParticipantName,
    revokeReason,
    setRevokeReason,
    emailForm,
    setEmailForm,
    sendingEmail,
    newEvent,
    setNewEvent,
    handleEditEvent,
    handleSaveEvent,
    handleSyncMonday,
    handleCancelEdit,
    handleCSVUpload,
    handleApproveAttendance,
    openWithdrawDialog,
    handleWithdrawApproval,
    handleSendBulkEmail,
    handleCreateEvent,
    handleDeleteEvent,
    handleRevokeApprovalWithReason,
    handleApprovalCheckboxChange,
    handleToggleAdminRights,
    handleSuspendParticipant,
    handleDeleteParticipant,
    handleResetPassword,
    handleRefreshEvents,
    handleClearAllScrapedEvents,
    handleUpdateScrapedStatus,
    handleUpdateEventStatus,
    handleDeleteScrapedEvent,
    toggleRowExpand,
    handleSort,
    getSortedEvents,
    changeAlerts,
    alertRecipients,
    handleSendAlert,
    handleDismissAlert,
    handleSaveAlertRecipients,
    emailTemplates,
    handleSaveEmailTemplate,
    applicants,
    handleAddApplicant,
    handleDeleteApplicant,
    handleUpdateApplicant,
    handleSendWelcome,
    handleScanApplications,
    handleUploadApplicantsCSV,
    handleBulkWelcome,
    handleExportApplicantsCSV,
    handleArchiveApplicant,
    handleUnarchiveApplicant,
    handleAutoArchive,
    archivedApplicants,
    archiveStats,
    handleSwitchToCrew,
    handleUpdateSignupSettings,
    crewVisibility,
    handleSetCrewVisibility,
  } = useAdminDashboard();
  const [activeTab, setActiveTab] = useState('home');
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  if (loading) {
    return (
      <div className="min-h-screen bg-crew-black flex items-center justify-center">
        <div className="text-zinc-500 text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const getAdminEmail = () => {
    try {
      const adminData = JSON.parse(localStorage.getItem('adminData') || '{}');
      return adminData.email || "Admin";
    } catch {
      return "Admin";
    }
  };

  const adminEmail = getAdminEmail();

  return (
    <div className="min-h-screen bg-crew-black relative grain-overlay">
      <Navigation userType="admin" userName={adminEmail} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Mobile section picker (clean nav on phones, portrait or landscape) */}
          <div className="md:hidden mb-6">
            <label className="text-xs uppercase font-bold text-zinc-500">Section</label>
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full mt-1 bg-crew-zinc border border-zinc-700 text-white rounded-sm h-12 px-3 text-base focus:border-crew-yellow focus:outline-none"
              data-testid="mobile-section-select"
            >
              <option value="home">Home</option>
              <option value="attendees">EVENT CREW</option>
              <option value="attendance-requests">EVENT Requests</option>
              <option value="participants">Crew Approval</option>
              <option value="events">Event Management</option>
              <option value="signups">Sign-up Filters</option>
              <option value="applicants">Crew Call Applicants</option>
              <option value="reports">Reports</option>
              <option value="administrators">Administrators</option>
              <option value="calendar-imports">Calendar Imports</option>
              <option value="email">Email Crew</option>
              <option value="alerts">Change Alerts</option>
            </select>
          </div>
          <TabsList className="hidden md:flex bg-crew-zinc border border-zinc-800 mb-8 flex-wrap h-auto">
            <TabsTrigger value="home" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="home-tab">
              <Home className="mr-2 h-4 w-4" /> Home
            </TabsTrigger>
            <TabsTrigger value="attendees" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="attendees-tab">
              <Users className="mr-2 h-4 w-4" /> EVENT CREW
            </TabsTrigger>
            <TabsTrigger value="attendance-requests" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="attendance-requests-tab">
              <Check className="mr-2 h-4 w-4" /> EVENT Requests
              {attendanceRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {attendanceRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="participants" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="participants-tab">
              <Users className="mr-2 h-4 w-4" /> Crew Approval
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="events-tab">
              <Calendar className="mr-2 h-4 w-4" /> Event Management
            </TabsTrigger>
            <TabsTrigger value="signups" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="signups-tab">
              <ClipboardList className="mr-2 h-4 w-4" /> Sign-up Filters
            </TabsTrigger>
            <TabsTrigger value="applicants" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="applicants-tab">
              <UserPlus className="mr-2 h-4 w-4" /> Crew Call Applicants
              {(applicants || []).filter(a => !a.welcomed).length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {(applicants || []).filter(a => !a.welcomed).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="reports-tab">
              <BarChart3 className="mr-2 h-4 w-4" /> Reports
            </TabsTrigger>
            <TabsTrigger value="administrators" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="administrators-tab">
              <Lock className="mr-2 h-4 w-4" /> Administrators
            </TabsTrigger>
            <TabsTrigger value="calendar-imports" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="calendar-imports-tab">
              <Mail className="mr-2 h-4 w-4" /> Calendar Imports
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="email-tab">
              <Send className="mr-2 h-4 w-4" /> Email Crew
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-crew-yellow data-[state=active]:text-black" data-testid="alerts-tab">
              <Bell className="mr-2 h-4 w-4" /> Change Alerts
              {(changeAlerts || []).filter(a => a.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {(changeAlerts || []).filter(a => a.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home" data-testid="home-content">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-crew-yellow">DASHBOARD</h2>
              <p className="text-zinc-400 text-sm">Tap a button to jump to a section.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { v: "attendees", label: "EVENT CREW", icon: Users },
                { v: "attendance-requests", label: "EVENT REQUESTS", icon: Check },
                { v: "participants", label: "CREW APPROVAL", icon: Shield },
                { v: "events", label: "EVENT MANAGEMENT", icon: Calendar },
                { v: "signups", label: "SIGN-UP FILTERS", icon: ClipboardList },
                { v: "applicants", label: "CREW CALL APPLICANTS", icon: UserPlus },
                { v: "reports", label: "REPORTS", icon: BarChart3 },
              ].map(({ v, label, icon: Icon }) => (
                <button
                  key={v}
                  onClick={() => setActiveTab(v)}
                  data-testid={`home-btn-${v}`}
                  className="group flex flex-col items-center justify-center gap-3 h-40 rounded-sm border-2 border-zinc-700 bg-crew-zinc hover:border-crew-yellow hover:bg-crew-yellow/10 transition-all active:scale-95"
                >
                  <Icon className="w-10 h-10 text-crew-yellow" />
                  <span className="text-lg font-bold text-white text-center px-2 uppercase tracking-tight">{label}</span>
                </button>
              ))}
            </div>
          </TabsContent>

          <AttendanceRequestsTab
            attendanceRequests={attendanceRequests}
            handleApproveAttendance={handleApproveAttendance}
            openWithdrawDialog={openWithdrawDialog}
          />

          <ParticipantsTab
            participants={participants}
            currentAdminEmail={currentAdminEmail}
            handleApprovalCheckboxChange={handleApprovalCheckboxChange}
            handleToggleAdminRights={handleToggleAdminRights}
            handleSuspendParticipant={handleSuspendParticipant}
            handleDeleteParticipant={handleDeleteParticipant}
            handleResetPassword={handleResetPassword}
          />

          <AdministratorsTab
            administrators={administrators}
            currentAdminEmail={currentAdminEmail}
            handleToggleAdminRights={handleToggleAdminRights}
          />

          <EventsTab
            events={events}
            dialogOpen={dialogOpen}
            mondaySyncStatus={mondaySyncStatus}
            expandedRows={expandedRows}
            syncingMonday={syncingMonday}
            editingEventId={editingEventId}
            editEventData={editEventData}
            newEvent={newEvent}
            setDialogOpen={setDialogOpen}
            setEditEventData={setEditEventData}
            setNewEvent={setNewEvent}
            handleSyncMonday={handleSyncMonday}
            handleEditEvent={handleEditEvent}
            handleSaveEvent={handleSaveEvent}
            handleCancelEdit={handleCancelEdit}
            handleCreateEvent={handleCreateEvent}
            handleDeleteEvent={handleDeleteEvent}
            handleUpdateEventStatus={handleUpdateEventStatus}
            toggleRowExpand={toggleRowExpand}
          />

          <SignupsTab
            events={events}
            handleUpdateSignupSettings={handleUpdateSignupSettings}
            crewVisibility={crewVisibility}
            handleSetCrewVisibility={handleSetCrewVisibility}
          />

          <AttendeesTab
            attendanceRequests={attendanceRequests}
            attendeeSearch={attendeeSearch}
            setAttendeeSearch={setAttendeeSearch}
            attendeeSortField={attendeeSortField}
            attendeeSortDir={attendeeSortDir}
            setAttendeeSortField={setAttendeeSortField}
            setAttendeeSortDir={setAttendeeSortDir}
          />

          <CalendarImportsTab
            events={events}
            scrapedEvents={scrapedEvents}
            scanning={scanning}
            sortField={sortField}
            sortDir={sortDir}
            expandedRows={expandedRows}
            tableRef={tableRef}
            uploading={uploading}
            fileInputRef={fileInputRef}
            handleCSVUpload={handleCSVUpload}
            handleRefreshEvents={handleRefreshEvents}
            handleClearAllScrapedEvents={handleClearAllScrapedEvents}
            handleUpdateScrapedStatus={handleUpdateScrapedStatus}
            handleDeleteScrapedEvent={handleDeleteScrapedEvent}
            toggleRowExpand={toggleRowExpand}
            handleSort={handleSort}
            getSortedEvents={getSortedEvents}
          />

          <EmailTab
            emailForm={emailForm}
            sendingEmail={sendingEmail}
            setEmailForm={setEmailForm}
            handleSendBulkEmail={handleSendBulkEmail}
            emailTemplates={emailTemplates}
            handleSaveEmailTemplate={handleSaveEmailTemplate}
            events={events}
          />

          <AlertsTab
            changeAlerts={changeAlerts}
            alertRecipients={alertRecipients}
            emailTemplates={emailTemplates}
            handleSendAlert={handleSendAlert}
            handleDismissAlert={handleDismissAlert}
            handleSaveAlertRecipients={handleSaveAlertRecipients}
          />

          <ApplicantsTab
            applicants={applicants}
            archivedApplicants={archivedApplicants}
            archiveStats={archiveStats}
            emailTemplates={emailTemplates}
            handleAddApplicant={handleAddApplicant}
            handleDeleteApplicant={handleDeleteApplicant}
            handleUpdateApplicant={handleUpdateApplicant}
            handleSendWelcome={handleSendWelcome}
            handleScanApplications={handleScanApplications}
            handleUploadApplicantsCSV={handleUploadApplicantsCSV}
            handleBulkWelcome={handleBulkWelcome}
            handleExportApplicantsCSV={handleExportApplicantsCSV}
            handleArchiveApplicant={handleArchiveApplicant}
            handleUnarchiveApplicant={handleUnarchiveApplicant}
            handleAutoArchive={handleAutoArchive}
            onOpenCSVImport={() => setCsvImportOpen(true)}
          />

          <ReportsTab />
        </Tabs>
      </div>

      {/* CSV Import Dialog */}
      <CSVImportDialog 
        open={csvImportOpen} 
        onClose={() => setCsvImportOpen(false)}
        onImportComplete={() => {
          setCsvImportOpen(false);
          // Refresh data will happen via the hook
        }}
      />

      {/* Withdraw Approval Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className={withdrawingAttendance?.status === 'pending' ? "text-red-500" : "text-crew-yellow"}>
              {withdrawingAttendance?.status === 'pending' ? 'Withdraw Request' : 'Withdraw Approval'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {withdrawingAttendance && (
                <>
                  {withdrawingAttendance.status === 'pending' ? (
                    <>Withdraw attendance request from <span className="text-white font-medium">{withdrawingAttendance.participant_name}</span> for <span className="text-crew-yellow font-medium">{withdrawingAttendance.event_name}</span>?</>
                  ) : (
                    <>Withdraw approval for <span className="text-white font-medium">{withdrawingAttendance.participant_name}</span> attending <span className="text-crew-yellow font-medium">{withdrawingAttendance.event_name}</span>?</>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="withdraw-reason" className="text-sm text-zinc-400">
                Reason for withdrawal (optional)
              </Label>
              <Textarea
                id="withdraw-reason"
                placeholder="e.g., Schedule conflict, event capacity changed, crew member request..."
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                className="mt-2 bg-black border-zinc-700 text-white placeholder:text-zinc-600"
                rows={3}
                data-testid="withdraw-reason-input"
              />
              <p className="text-xs text-zinc-500 mt-2">
                If provided, this reason will be included in the notification email sent to the crew member.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setWithdrawDialogOpen(false)}
              className="border-zinc-700 text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleWithdrawApproval}
              className={withdrawingAttendance?.status === 'pending' ? "bg-red-500 hover:bg-red-400 text-white font-bold" : "bg-yellow-500 hover:bg-yellow-400 text-black font-bold"}
              data-testid="confirm-withdraw-button"
            >
              {withdrawingAttendance?.status === 'pending' ? 'Withdraw Request' : 'Withdraw Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Crew Approval Dialog */}
      <Dialog open={revokeApprovalDialogOpen} onOpenChange={setRevokeApprovalDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-500">Revoke Crew Approval</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Revoke approval for <span className="text-white font-medium">{revokeParticipantName}</span>? They will no longer be able to log in or attend events.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="revoke-reason" className="text-sm text-zinc-400">
                Reason for revoking approval (optional)
              </Label>
              <Textarea
                id="revoke-reason"
                placeholder="e.g., Inactive member, policy violation, member request..."
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                className="mt-2 bg-black border-zinc-700 text-white placeholder:text-zinc-600"
                rows={3}
                data-testid="revoke-reason-input"
              />
              <p className="text-xs text-zinc-500 mt-2">
                If provided, this reason will be included in the notification email sent to the crew member.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRevokeApprovalDialogOpen(false)}
              className="border-zinc-700 text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRevokeApprovalWithReason}
              className="bg-red-500 hover:bg-red-400 text-white font-bold"
              data-testid="confirm-revoke-button"
            >
              Revoke Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default AdminDashboard;
